import IconButton from "@mui/material/IconButton";
import type { SelectProps } from "@mui/material/Select";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { ReactNode } from "react";

import { PublicSvgIcon } from "~/components/public_svg_icon";

export type ClearableSelectWithPlaceholderProps<T> = {
  placeholder: ReactNode;
  isClearable?: boolean;
  onClear?: () => void;
} & SelectProps<T>;

export function ClearableSelectWithPlaceholder<T = unknown>(
  props: ClearableSelectWithPlaceholderProps<T> & { "data-testid"?: string },
) {
  const {
    placeholder,
    displayEmpty: isDisplayEmpty = true,
    renderValue: userRenderValue,
    isClearable = true,
    onClear,
    "data-testid": dataTestId,
    ...rest
  } = props;

  const renderValue = (selected: unknown) => {
    const isEmpty =
      selected === "" ||
      selected === null ||
      selected === undefined ||
      (Array.isArray(selected) && selected.length === 0);

    if (isEmpty) {
      return (
        <Typography color="text.disabled" variant="body1" component="span">
          {placeholder}
        </Typography>
      );
    }

    const content = userRenderValue
      ? userRenderValue(selected as T)
      : (selected as ReactNode);
    const shouldRenderClear = isClearable && !isEmpty;

    return (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          width: "100%",
          gap: 1,
          "&:hover .clear-btn": { visibility: "visible" },
        }}
      >
        {content}
        {shouldRenderClear ? (
          <IconButton
            tabIndex={-1}
            size="small"
            className="clear-btn"
            sx={{ visibility: "hidden" }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (onClear) {
                onClear();
              } else if (rest.onChange) {
                rest.onChange(
                  { target: { value: "" as T } } as Parameters<
                    NonNullable<typeof rest.onChange>
                  >[0],
                  "",
                );
              }
            }}
          >
            <PublicSvgIcon name="clear" />
          </IconButton>
        ) : null}
      </Stack>
    );
  };

  return (
    <Select
      {...rest}
      displayEmpty={isDisplayEmpty}
      renderValue={renderValue}
      SelectDisplayProps={{
        ...rest.SelectDisplayProps,
        ...(dataTestId ? { "data-testid": dataTestId } : {}),
      }}
      sx={{
        "& .clear-btn": { visibility: "hidden" },
        "&:hover .clear-btn": { visibility: "visible" },
        "&.Mui-focused .clear-btn": { visibility: "visible" },
        ...(rest.sx || {}),
      }}
    />
  );
}
